<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />
  <div v-else class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-info-circle"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل صفحة "من نحن"</h1>
        <p class="form-page-header__subtitle">تحرير المحتوى الظاهر في صفحة "من نحن" بالموقع العام</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">المحتوى</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">عنوان الصفحة <span class="optional">اختياري</span></label>
                  <InputText v-model="title" class="w-100" placeholder="من نحن" />
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">المحتوى <span class="required">*</span></label>
                  <Editor v-model="content" editorStyle="height: 320px" />
                  <span class="field-error" v-if="v$.content.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال المحتوى
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-image"></i></div>
            <span class="form-card__title">صورة الصفحة</span>
          </div>
          <div class="form-card__body">
            <img v-if="currentImageLink && !imageFile" :src="currentImageLink" alt="" class="current-image-preview mb-3" />
            <div :class="['upload-zone', { 'upload-zone--has-file': imageFile }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageFile ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageFile ? imageFile.name : 'اضغط لاختيار صورة' }}</p>
              <p class="upload-zone__hint" v-if="!imageFile">PNG, JPG, GIF</p>
              <input type="file" @change="onImageChange" accept="image/*" />
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-search"></i></div>
            <span class="form-card__title">تحسين محركات البحث (SEO)</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">عنوان SEO <span class="optional">اختياري</span></label>
                  <InputText v-model="metaTitle" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">وصف SEO <span class="optional">اختياري</span></label>
                  <Textarea v-model="metaDescription" rows="2" class="w-100" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Editor from 'primevue/editor';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';

export default {
  name: 'AboutEdit',
  components: { Breadcrumb, Loading, InputText, Textarea, Editor },
  data() {
    return {
      v$: useVuelidate(),
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'من نحن', path: '/about' },
        { name: 'تعديل', path: '/about/edit' },
      ],
      title: '',
      content: '',
      imageFile: null,
      currentImageLink: null,
      metaTitle: '',
      metaDescription: '',
      formSending: false,
      loading: true,
    };
  },
  validations() {
    return {
      content: { required },
    };
  },
  methods: {
    onImageChange(e) {
      this.imageFile = e.target.files[0] || null;
    },
    async fetchAbout() {
      this.loading = true;
      try {
        const { data } = await axios.get('/admin/about-page');
        const about = data.data;
        this.title = about?.title ?? '';
        this.content = about?.content ?? '';
        this.currentImageLink = about?.image_link ?? null;
        this.metaTitle = about?.meta_title ?? '';
        this.metaDescription = about?.meta_description ?? '';
      } finally {
        this.loading = false;
      }
    },
    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;
      this.formSending = true;
      try {
        const fd = new FormData();
        fd.append('title', this.title ?? '');
        fd.append('content', this.content ?? '');
        fd.append('meta_title', this.metaTitle ?? '');
        fd.append('meta_description', this.metaDescription ?? '');
        if (this.imageFile) fd.append('image', this.imageFile);

        await axios.post('/admin/about-page', fd);
        Swal.fire({ icon: 'success', title: 'تم حفظ التعديلات بنجاح', timer: 1500, showConfirmButton: false });
        this.$router.push('/about');
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      } finally {
        this.formSending = false;
      }
    },
  },
  mounted() {
    this.fetchAbout();
  },
};
</script>

<style scoped>
.current-image-preview { max-width: 320px; border-radius: var(--radius-md); border: 1px solid var(--surface-border); display: block; }
</style>
